/**
 * Client questionnaire entry point, under the GatherBanner (design: questionnaire-design.md section 7.2).
 *
 * One Google Sheet per client: create it, send the link, import the answers as REVIEWABLE DRAFTS (nothing reaches
 * the live Brand Bible until a person confirms a draft). The .xlsx download / upload is the fallback.
 *
 * Hidden unless the viewer can edit and `GET /questionnaire/info` answered (404 with the backend flag off, 403 for
 * a viewer, or any error hides it). Tenant-scoped like the GatherBanner and the tenant-switch fix: every request is
 * made for the tenant of the click, and a continuation that lands after the person switched client changes nothing.
 * The long jobs (import, upload) run through `store.runDraftJob(tenant, 'questionnaire', job)`, which holds the
 * page's autosave and shares the one run slot with the web gather.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { bbSourcesKeys } from '@/hooks/bbSourcesKeys';
import { apiErrorCode, brandBibleV2Service } from '@/services/brandBibleV2Service';
import { useBrandBibleWizardStore } from '@/stores/brandBibleWizardStore';
import type { QuestionnaireInfo, QuestionnaireRunSummary } from '@/features/brand-bible-wizard/types';
import { DraftRunSummary } from './DraftRunSummary';
import { SummaryBoundary } from './SummaryBoundary';

const MAX_FILE_BYTES = 2 * 1024 * 1024;
const XLSX_ACCEPT = '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const FILLED_V1 =
  'This Brand Bible already has content. Questionnaire drafts are only available for empty or already-migrated Brand Bibles.';

const ERROR_WORDS: Record<string, string> = {
  no_sheet: 'Create the questionnaire sheet first.',
  not_eligible: FILLED_V1,
  sheet_trashed: 'The questionnaire sheet was deleted. Create a new questionnaire to send the client a fresh one.',
  wrong_tenant: 'That questionnaire belongs to a different client.',
  template_outdated: 'This questionnaire is out of date. Create a new questionnaire.',
  bad_template: "That isn't the Brand Bible questionnaire.",
  drive_not_ready: "Google Drive isn't set up for questionnaires here yet.",
  drive_env_mismatch: "Google Drive isn't set up for questionnaires here yet.",
  google_unavailable: "Google isn't available right now. Try again in a minute.",
  file_too_large: 'That file is too large (2 MB at most).',
  unsupported_type: 'Please upload an .xlsx file.',
  bad_file: "We couldn't read that file. Download the .xlsx again and fill that one in.",
  zip_bomb: "We couldn't read that file. Download the .xlsx again and fill that one in.",
};
const GENERIC = 'Something went wrong. Nothing was imported; you can try again.';

const isHttp = (url: string) => /^https?:\/\//i.test(url);
const older = (a: string, b: string) => (Number.isFinite(Number(a)) && Number.isFinite(Number(b)) ? Number(a) < Number(b) : a < b);

export function QuestionnairePanel({ tenantId, canEdit }: { tenantId: string; canEdit: boolean }) {
  const enabled = canEdit && !!tenantId;
  const queryClient = useQueryClient();
  const jobRunning = useBrandBibleWizardStore((s) => s.gatherRunning);
  // The tenant this panel shows NOW: a continuation of a click made for another tenant must not touch it.
  const tenantRef = useRef(tenantId);
  tenantRef.current = tenantId;
  const fileInput = useRef<HTMLInputElement | null>(null);

  const [info, setInfo] = useState<QuestionnaireInfo | null>(null);
  const [result, setResult] = useState<QuestionnaireRunSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inProgress, setInProgress] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmRecreate, setConfirmRecreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [running, setRunning] = useState(false);
  const [trashed, setTrashed] = useState(false);

  const askInfo = useCallback(async () => {
    try {
      const next = await brandBibleV2Service.getQuestionnaireInfo(tenantId);
      if (tenantRef.current !== tenantId) return;
      setInfo(next);
    } catch {
      // Before any answer there is nothing to show (hidden). After one, keep what we have.
    }
  }, [tenantId]);

  useEffect(() => {
    setInfo(null);
    setResult(null);
    setError(null);
    setInProgress(false);
    setCopied(false);
    setConfirmRecreate(false);
    setCreating(false);
    setRunning(false);
    setTrashed(false);
    if (enabled) void askInfo();
  }, [enabled, askInfo]);

  if (!enabled || !info) return null;

  const busy = creating || running || jobRunning;
  const sheet = info.sheet;
  const sheetLink = sheet && isHttp(sheet.url) ? sheet.url : null;
  const showError = (err: unknown, startedFor: string): boolean => {
    if (tenantRef.current !== startedFor) return false;
    const code = apiErrorCode(err);
    if (code === 'gather_in_progress') setInProgress(true);
    else setError((code && ERROR_WORDS[code]) || GENERIC);
    if (code === 'no_sheet') void askInfo();
    if (code === 'sheet_trashed') setTrashed(true);
    return true;
  };

  const create = async (recreate: boolean) => {
    const startedFor = tenantId;
    setError(null);
    setInProgress(false);
    setConfirmRecreate(false);
    setCreating(true);
    try {
      await brandBibleV2Service.createQuestionnaireSheet(startedFor, recreate);
      if (tenantRef.current !== startedFor) return;
      await askInfo();
    } catch (err) {
      showError(err, startedFor);
    } finally {
      if (tenantRef.current === startedFor) setCreating(false);
    }
  };

  const runJob = async (job: (startedFor: string) => Promise<QuestionnaireRunSummary>) => {
    const startedFor = tenantId;
    setResult(null);
    setError(null);
    setInProgress(false);
    setTrashed(false);
    setRunning(true);
    try {
      const done = await useBrandBibleWizardStore
        .getState()
        .runDraftJob(startedFor, 'questionnaire', () => job(startedFor));
      if (tenantRef.current !== startedFor) return;
      if (done) {
        setResult(done);
        await askInfo();
      } else {
        // The store found a job already running for this client and did not start another one.
        setInProgress(true);
      }
    } catch (err) {
      showError(err, startedFor);
    } finally {
      // The run (even a failed one) may have written drafts and their sources.
      void queryClient.invalidateQueries({ queryKey: bbSourcesKeys.list(startedFor) });
      if (tenantRef.current === startedFor) setRunning(false);
    }
  };

  const copyLink = async () => {
    if (!sheetLink) return;
    setError(null);
    try {
      await navigator.clipboard.writeText(sheetLink);
      setCopied(true);
    } catch {
      setError("Couldn't copy the link. Use Open questionnaire and copy it from the address bar.");
    }
  };

  const download = async () => {
    const startedFor = tenantId;
    setError(null);
    try {
      const blob = await brandBibleV2Service.downloadQuestionnaireTemplate(startedFor);
      if (tenantRef.current !== startedFor) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'brand-bible-questionnaire.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      showError(err, startedFor);
    }
  };

  const onFile = (file: File | undefined) => {
    if (fileInput.current) fileInput.current.value = '';
    if (!file) return;
    if (!/\.xlsx$/i.test(file.name)) {
      setError('Please choose an .xlsx file (the downloaded questionnaire, filled in).');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError('That file is larger than 2 MB. Send the downloaded questionnaire back, not a different file.');
      return;
    }
    void runJob((t) => brandBibleV2Service.uploadQuestionnaire(t, file));
  };

  const button = 'rounded-[7px] border border-border bg-card px-3 py-1.5 text-[12px] font-semibold text-foreground hover:bg-warm-bg disabled:opacity-60';
  const primary = 'rounded-[7px] bg-coral px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-coral-hover disabled:opacity-60';
  const runsOff = busy || !info.eligible;

  return (
    <div data-testid="questionnaire-panel" className="flex flex-col gap-2 rounded-[10px] border border-border px-4 py-3">
      <div>
        <div className="text-[13px] font-semibold">Ask the client to fill in a questionnaire</div>
        <div className="text-xs text-muted-foreground">
          {sheet
            ? 'Send this link to the client. Anyone with the link can edit it. When they are done, import their answers as drafts.'
            : 'We create a Google Sheet just for this client. Their answers become drafts you review; nothing goes live until you confirm it.'}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {!sheet && (
          <button type="button" data-testid="questionnaire-create" disabled={busy || !info.drive_ready}
            onClick={() => void create(false)} className={primary}>
            Create questionnaire sheet
          </button>
        )}
        {sheet && sheetLink && (
          <a data-testid="questionnaire-open" href={sheetLink} target="_blank" rel="noopener noreferrer"
            className="rounded-[7px] bg-coral px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-coral-hover">
            Open questionnaire
          </a>
        )}
        {sheet && sheetLink && (
          <button type="button" data-testid="questionnaire-copy" onClick={() => void copyLink()} className={button}>
            Copy link
          </button>
        )}
        {sheet && (
          <button type="button" data-testid="questionnaire-import" disabled={runsOff}
            onClick={() => void runJob((t) => brandBibleV2Service.importQuestionnaire(t))} className={button}>
            Import answers
          </button>
        )}
        <button type="button" data-testid="questionnaire-download" onClick={() => void download()} className={button}>
          Download .xlsx
        </button>
        <button type="button" data-testid="questionnaire-upload" disabled={runsOff}
          onClick={() => fileInput.current?.click()} className={button}>
          Upload filled .xlsx
        </button>
        <input ref={fileInput} data-testid="questionnaire-upload-input" type="file" accept={XLSX_ACCEPT} className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])} />
        {sheet && (trashed || older(sheet.template_version, info.latest_version)) && (
          <button type="button" data-testid="questionnaire-recreate" disabled={busy}
            onClick={() => setConfirmRecreate(true)} className="text-[11px] text-muted-foreground underline">
            Create a new questionnaire
          </button>
        )}
      </div>

      {confirmRecreate && (
        <div data-testid="questionnaire-recreate-confirm" className="flex flex-col gap-1.5 rounded-[7px] border border-border px-3 py-2 text-[12px]">
          <span>
            A new questionnaire replaces this one. The answers in the old sheet are lost unless you import them first.
          </span>
          <span className="flex gap-2">
            <button type="button" data-testid="questionnaire-recreate-yes" onClick={() => void create(true)} className={primary}>
              Create a new questionnaire
            </button>
            <button type="button" data-testid="questionnaire-recreate-no" onClick={() => setConfirmRecreate(false)} className={button}>
              Keep this one
            </button>
          </span>
        </div>
      )}

      {running && (
        <div data-testid="questionnaire-running" className="text-[12px] text-muted-foreground">
          Reading the questionnaire… this can take a minute. You can keep this page open.
        </div>
      )}
      {!info.eligible && info.reason === 'filled_v1' && (
        <div data-testid="questionnaire-disabled-reason" className="text-[12px] text-muted-foreground">{FILLED_V1}</div>
      )}
      {!info.drive_ready && (
        <div data-testid="questionnaire-not-ready" className="text-[12px] text-muted-foreground">
          Google Drive isn't set up for questionnaires here yet. You can still download the .xlsx and send that.
        </div>
      )}
      {inProgress && (
        <div data-testid="questionnaire-in-progress" className="text-[12px] text-muted-foreground">
          A draft run for this client is already in progress. Check back in a few minutes.
        </div>
      )}
      {error && (
        <div data-testid="questionnaire-error" className="text-[12px] text-destructive">{error}</div>
      )}
      {copied && (
        <div data-testid="questionnaire-copied" className="text-[12px] text-muted-foreground">Link copied</div>
      )}

      <SummaryBoundary resetKey={result}>
        <DraftRunSummary result={result} source={{ kind: 'questionnaire', label: 'the questionnaire' }} testIdPrefix="questionnaire" />
      </SummaryBoundary>
    </div>
  );
}
